/**
 * Reusable "Submit for Approval" dialog.
 *
 * Opens a small form (request type, title, amount, reason) and POSTs
 * to `/api/approvals/requests`. On success it shows a toast with the
 * generated request_code (A-1042 style) so the user knows their
 * filing landed in the approver's Inbox.
 *
 * Designed to be dropped into any workflow that needs sign-off:
 *
 *     <RequestApprovalDialog
 *       open={open}
 *       onOpenChange={setOpen}
 *       defaultType="discount_approval"
 *       defaultTitle={`Discount on deal ${deal.name}`}
 *       defaultAmount={discountValue}
 *       relatedId={deal.id}
 *       relatedType="deal"
 *     />
 *
 * Every default is overridable from inside the dialog so the user
 * can tweak before submitting — the props are just a head start.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// The 6 built-in request types from the backend. Kept here as a
// fallback so the dropdown works before the /api/approvals/types
// call resolves. The component still queries /types on mount and
// merges in any per-tenant labels.
const DEFAULT_TYPES = [
  { request_type: 'commission_approval',       label: 'Commission approval' },
  { request_type: 'discount_approval',         label: 'Discount approval' },
  { request_type: 'refund_approval',           label: 'Refund approval' },
  { request_type: 'bulk_action_approval',      label: 'Bulk action approval' },
  { request_type: 'lead_reassignment_approval',label: 'Lead reassignment approval' },
  { request_type: 'role_change_approval',      label: 'Role change approval' },
];

// Request types where an `amount` field makes sense. Hide the input
// for types where it doesn't (lead reassignment, role change).
const AMOUNT_TYPES = new Set([
  'commission_approval',
  'discount_approval',
  'refund_approval',
]);

export default function RequestApprovalDialog({
  open,
  onOpenChange,
  defaultType = 'commission_approval',
  defaultTitle = '',
  defaultAmount = '',
  defaultReason = '',
  relatedId = null,
  relatedType = null,
  details = null,
  onSubmitted,
}) {
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [requestType, setRequestType] = useState(defaultType);
  const [title, setTitle] = useState(defaultTitle);
  const [amount, setAmount] = useState(defaultAmount);
  const [reason, setReason] = useState(defaultReason);
  const [submitting, setSubmitting] = useState(false);

  const token = sessionStorage.getItem('auth_token');

  // Reset form when the dialog opens with new defaults
  useEffect(() => {
    if (!open) return;
    setRequestType(defaultType);
    setTitle(defaultTitle);
    setAmount(defaultAmount);
    setReason(defaultReason);
  }, [open, defaultType, defaultTitle, defaultAmount, defaultReason]);

  // Pull the per-tenant types (labels may be customised by admin)
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/approvals/types?token=${token}`);
        const list = res.data?.data?.types;
        if (!cancelled && Array.isArray(list) && list.length > 0) {
          setTypes(list);
        }
      } catch {
        /* keep DEFAULT_TYPES — non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  const submit = async () => {
    if (!requestType || !title.trim() || !reason.trim()) {
      toast.error('Type, title and reason are required.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        request_type: requestType,
        title: title.trim(),
        reason: reason.trim(),
        related_id: relatedId || undefined,
        related_type: relatedType || undefined,
        details: details || undefined,
      };
      if (AMOUNT_TYPES.has(requestType) && amount !== '' && amount !== null) {
        const parsed = Number(amount);
        if (!Number.isNaN(parsed)) body.amount = parsed;
      }
      const res = await axios.post(
        `${API}/api/approvals/requests?token=${token}`,
        body,
      );
      const doc = res.data?.data;
      const code = doc?.request_code || 'request';
      toast.success(`Submitted for approval (${code})`, {
        description: `Approvers in your org will see this in their Inbox.`,
      });
      onOpenChange(false);
      if (onSubmitted) onSubmitted(doc);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Failed to submit approval request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const showAmount = AMOUNT_TYPES.has(requestType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-violet-500" />
            Submit for Approval
          </DialogTitle>
          <DialogDescription>
            Files a request to the eligible approvers (HOD / manager / admin
            depending on type). You'll get a request code and they'll see it
            in their Approvals Inbox.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500">Request type</Label>
            <Select value={requestType} onValueChange={setRequestType}>
              <SelectTrigger className="mt-1 h-9">
                <SelectValue placeholder="Pick a type" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.request_type} value={t.request_type}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short label the approver sees first"
              className="mt-1 h-9"
            />
          </div>

          {showAmount && (
            <div>
              <Label className="text-xs text-slate-500">Amount (AED)</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="mt-1 h-9"
              />
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500">Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is this needed? The approver reads this before deciding."
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Submitting</> : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
